import { type DatiSessioneUtente } from '@framework/api-client/api/users/schemas/datiSessioneUtente.ts';
import { EntityMachine } from '@framework/common-web/fsm/EntityMachine.ts';
import { type IDelegationData } from './DelegationLogic.ts';

export function isManager(
  record: Maybe<IDelegationData>,
  user: Maybe<DatiSessioneUtente>,
) {
  if (!record) return false;
  return Boolean(
    record.manager?.fiscalCode &&
      record.manager?.fiscalCode === user?.utenteLoggato?.codiceFiscale,
  );
}

/**
 * Ritorna vero se l'utente è *uno dei* titolari della richiesta.
 */
export function isApplicant(
  record: Maybe<IDelegationData>,
  user: Maybe<DatiSessioneUtente>,
) {
  if (!record) return false;

  if (
    record.applicant?.some(
      applicant =>
        applicant.fiscalCode &&
        applicant.fiscalCode === user?.utenteLoggato?.codiceFiscale,
    )
  ) {
    return true;
  }

  return false;
}

/**
 * Ritorna vero se l'utente è il titolare *principale* della richiesta.
 */
function isMainApplicant(
  record: Maybe<IDelegationData>,
  user: Maybe<DatiSessioneUtente>,
) {
  if (!record) return false;

  const mainApplicant = record.applicant?.find(applicant => applicant.isMain);
  if (!mainApplicant) return false;

  if (
    mainApplicant.fiscalCode &&
    mainApplicant.fiscalCode === user?.utenteLoggato?.codiceFiscale
  ) {
    return true;
  }

  return false;
}

function isAttorney(
  record: Maybe<IDelegationData>,
  user: Maybe<DatiSessioneUtente>,
) {
  if (!record) return false;

  if (
    record.attorney?.fiscalCode &&
    record.attorney?.fiscalCode === user?.utenteLoggato?.codiceFiscale
  ) {
    return true;
  }

  return false;
}

export function getSubmissionDelegationRole(
  record: Maybe<IDelegationData>,
  user: Maybe<DatiSessioneUtente>,
) {
  if (isApplicant(record, user)) {
    return 'applicant';
  }
  if (isAttorney(record, user)) {
    return 'attorney';
  }
  if (isManager(record, user)) {
    return 'manager';
  }
  return 'delegate'; // Delegato sul singolo modulo per fallback
}

export function isDelegate(
  record: Maybe<IDelegationData>,
  user: Maybe<DatiSessioneUtente>,
) {
  return getSubmissionDelegationRole(record, user) === 'delegate';
}

/**
 * Ritorna vero se l'utente è il gestore, procuratore o titolare
 * della richiesta.
 */
function hasControl(
  record: Maybe<IDelegationData>,
  user: Maybe<DatiSessioneUtente>,
  options?: {
    // Se true, l'utente deve essere il titolare principale
    mainApplicant?: boolean;
  },
) {
  if (!record) return false;

  if (isManager(record, user)) {
    return true;
  }

  // Se il flag è attivo, l'utente deve essere il titolare principale
  if (options?.mainApplicant && isMainApplicant(record, user)) {
    return true;
  }

  if (!options?.mainApplicant && isApplicant(record, user)) {
    return true;
  }

  if (isAttorney(record, user)) {
    return true;
  }

  return false;
}

export enum DelegationMessageKey {
  SUBMIT_BUTTON_DISABLED = 'submit-button-disabled',
}

export const DelegationMachine = EntityMachine.for<
  IDelegationData,
  DatiSessioneUtente
>()
  .withState(record => {
    return '';
  })
  .withAction('.view', {
    // La visualizzazione è disponibile per:
    // gestore, procuratore, titolare
    allowed: (record, user) => {
      return { allowed: hasControl(record, user) };
    },
  })
  .withAction('.editManager', {
    // La modifica del gestore è disponibile per:
    // Gestore, procuratore, titolare principale
    allowed: (record, user) => {
      return { allowed: hasControl(record, user, { mainApplicant: true }) };
    },
  })
  .withAction('.editAttorney', {
    // La modifica del procuratore è disponibile per:
    // gestore o procuratore
    allowed: (record, user) => {
      return {
        allowed: isManager(record, user) || isAttorney(record, user),
      };
    },
  })
  .withAction('.editDelegates', {
    // La modifica alle deleghe è disponibile per:
    // gestore
    allowed: (record, user) => {
      return { allowed: isManager(record, user) };
    },
  })
  .withAction('.canSubmit', {
    allowed: (record, user) => {
      // Se l'utente è il titolare _principale_ può inviare
      if (isMainApplicant(record, user)) {
        return { allowed: true };
      }

      // Se l'utente è il procuratore deve avere il permesso di inviare
      if (isAttorney(record, user) && record?.attorney?.canSubmit) {
        return { allowed: true };
      }

      // Se sono il gestore (ma non sono né titolare né procuratore)
      // non posso inviare ma posso vedere il pulsante disabilitato
      if (isManager(record, user)) {
        return {
          allowed: false,
          message: DelegationMessageKey.SUBMIT_BUTTON_DISABLED,
        };
      }

      // Se l'utente è il gestore e non c'è il procuratore, può inviare
      // if (
      //   getSubmissionDelegationRole(record, user) === 'manager' &&
      //   !record.attorney?.fiscalCode
      // ) {
      //   return { allowed: false };
      // }

      return { allowed: false };
    },
  })
  .withAction('.canDuplicate', {
    // Controllo se l'utente è abilitato a duplicare la richiesta
    // (Pulsante "Duplica la Richiesta" nella pagina di dettaglio)
    allowed: (record, user) => {
      return { allowed: hasControl(record, user) };
    },
  });
